/** Sprint 5-c · 公司會議室 · 會議明細（資訊 + 與會者管理 + 進會議室/記錄/取消）。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, Divider, IconButton, List, ListItem, ListItemText, MenuItem,
  Paper, Select, Stack, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { useMessage } from '../../../contexts/MessageContext';
import { useAuth } from '../../../contexts/AuthContext';
import { formatLocal } from '../../../utils/dateHelper';
import { getMemberOptions, type MemberOption } from '../../../services/tenantUsersService';
import {
  addAttendee, cancelMeeting, getMeeting, listAttendees, MeetingAttendee, OfficeMeeting, removeAttendee,
} from './services/officeMeetingService';

const OfficeMeetingDetailPage: React.FC = () => {
  const { id } = useParams();
  const mid = Number(id);
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`officeMeeting.${k}`, d);
  const { showApiError, showSuccess } = useMessage();
  const { user } = useAuth();

  const [m, setM] = useState<OfficeMeeting | null>(null);
  const [attendees, setAttendees] = useState<MeetingAttendee[]>([]);
  const [users, setUsers] = useState<MemberOption[]>([]);
  const [addId, setAddId] = useState<number | ''>('');

  const isOwner = !!m && !!user && m.owner_user_id === user.id;
  const editable = !!m && m.status === 'scheduled';

  const load = useCallback(async () => {
    try {
      setM(await getMeeting(mid));
      setAttendees(await listAttendees(mid));
    } catch (err) { showApiError(err); }
  }, [mid, showApiError]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getMemberOptions().then(setUsers).catch(() => { /* silent ok: 成員清單載入失敗不阻擋明細 */ }); }, []);

  const doAdd = async () => {
    if (!addId) return;
    try { setAttendees(await addAttendee(mid, Number(addId))); setAddId(''); showSuccess('SYS020001', { name: tt('attendee', '與會者') }); }
    catch (err) { showApiError(err); }
  };
  const doRemove = async (uid: number) => {
    try { await removeAttendee(mid, uid); setAttendees(await listAttendees(mid)); }
    catch (err) { showApiError(err); }
  };
  const doCancel = async () => {
    try { await cancelMeeting(mid); showSuccess('SYS020001', { name: m?.title || '' }); load(); }
    catch (err) { showApiError(err); }
  };

  if (!m) return <Box sx={{ p: 3 }}><Typography color="text.secondary">{tt('loading', '載入中…')}</Typography></Box>;

  const attendeeIds = new Set(attendees.map((a) => a.user_id));
  const addable = users.filter((u) => !attendeeIds.has(u.id));

  return (
    <Box sx={{ p: 3, maxWidth: 820, mx: 'auto' }}>
      <Button size="small" onClick={() => nav('/ee_office_meeting')}>← {tt('back', '返回列表')}</Button>

      <Paper variant="outlined" sx={{ p: 2.5, mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="h5">{m.title}</Typography>
          <Chip size="small" label={tt(`status_${m.status}`, m.status)} />
          {m.public_meeting === false && (
            <Chip size="small" variant="outlined" label={`🔒 ${tt('secret', '秘密')}`} />
          )}
          {m.calendar_event_id && (
            <Chip size="small" variant="outlined" color="info" clickable
              label={tt('fromCalendar', '由日曆行程建立')}
              onClick={() => nav('/ee_my_calendar')} />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {m.meeting_no} · {tt('owner', '主辦')}：{m.owner_name}</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
          {m.chair_name && <Chip size="small" label={`${tt('chair', '主席')}：${m.chair_name}`} />}
          {m.recorder_name && <Chip size="small" variant="outlined" label={`${tt('recorder', '紀錄人員')}：${m.recorder_name}`} />}
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={0.5}>
          <Typography variant="body2"><strong>{tt('fStart', '開始')}：</strong>{formatLocal(m.start_at)}</Typography>
          <Typography variant="body2"><strong>{tt('fEnd', '結束')}：</strong>{formatLocal(m.end_at)}</Typography>
          {m.location && <Typography variant="body2"><strong>{tt('fLoc', '地點')}：</strong>{m.location}</Typography>}
          {m.description && <Typography variant="body2"><strong>{tt('fDesc', '說明')}：</strong>{m.description}</Typography>}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
          {(m.status === 'scheduled' || m.status === 'in_progress') && (
            <Button variant="contained" onClick={() => nav(`/ee_office_meeting/${mid}/room`)}>
              {tt('enterRoom', '進入會議室')}</Button>
          )}
          {m.status === 'ended' && (
            <Button variant="outlined" onClick={() => nav(`/ee_office_meeting/${mid}/minutes`)}>
              {tt('viewMinutes', '會議記錄')}</Button>
          )}
          {isOwner && m.status !== 'cancelled' && m.status !== 'ended' && (
            <Button color="error" onClick={doCancel}>{tt('cancel', '取消會議')}</Button>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>{tt('attendees', '與會者')}（{attendees.length}）</Typography>
        <List dense>
          {attendees.map((a) => (
            <ListItem key={a.user_id}
              secondaryAction={isOwner && editable && (
                <IconButton edge="end" size="small" onClick={() => doRemove(a.user_id)}><DeleteIcon fontSize="small" /></IconButton>
              )}>
              <ListItemText primary={a.name} />
            </ListItem>
          ))}
          {attendees.length === 0 && <Typography variant="body2" color="text.secondary">{tt('noAttendees', '（尚無與會者）')}</Typography>}
        </List>
        {isOwner && editable && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Select size="small" value={addId === '' ? '' : String(addId)} displayEmpty sx={{ minWidth: 220 }}
              onChange={(e) => { const v = String(e.target.value); setAddId(v === '' ? '' : Number(v)); }}>
              <MenuItem value=""><em>{tt('pickAttendee', '選擇成員加入…')}</em></MenuItem>
              {addable.map((u) => <MenuItem key={u.id} value={u.id}>{u.username}（{u.account}）</MenuItem>)}
            </Select>
            <Button startIcon={<AddIcon />} onClick={doAdd} disabled={!addId}>{tt('add', '加入')}</Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default OfficeMeetingDetailPage;
